import { Suspense, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import type { BusinessSummaryItem } from 'src/features/business/shared/schemas/business-item'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import { useDebouncedValue } from 'src/shared/hooks/use-debounced-value'

import { useGenericSearch } from '../api/use-generic-search'
import type { GenericSearchFilter } from '../api/use-generic-search-with-filter'
import { useGenericSearchWithFilter } from '../api/use-generic-search-with-filter'
import FacelessBusinessSearchResultItem from '../components/faceless-biz-search-result-item'
import FilterPillBar from '../components/ui/filter-pill-bar'
import SectionHeader from '../components/ui/section-header'
import UserSearchResultItem from '../components/user-search-result-item'
import FacelessBizSearchResultItemSkeleton from '../skeletons/faceless-biz-search-result-item-skeleton'
import GenericSearchResultsViewSkeleton from '../skeletons/generic-search-results-view-skeleton'
import UserSearchResultItemSkeleton from '../skeletons/user-search-result-item-skeleton'

interface GenericSearchResultsProps {
  searchText: string
}

const PAGE_SIZE = 20
const PREVIEW_COUNT = 4

interface GenericSearchResultsContentProps extends GenericSearchResultsProps {
  filter: GenericSearchFilter | 'all'
  onFilterChange: (filter: GenericSearchFilter | 'all') => void
}

const GenericSearchResultsContent = ({ searchText, filter, onFilterChange }: GenericSearchResultsContentProps) => {
  const debouncedSearchText = useDebouncedValue(searchText, 300)
  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 })
  const { data: searchResults } = useGenericSearch({ query: debouncedSearchText, limit: PAGE_SIZE })
  const genericSearchWithFilterQuery = useGenericSearchWithFilter({
    filter: filter === 'all' ? undefined : filter,
    query: debouncedSearchText,
    limit: PAGE_SIZE,
  })
  const { data: searchResultsWithFilter, isFetchingNextPage } = genericSearchWithFilterQuery

  const handleFilterPillChange = (nextFilter: string) => {
    onFilterChange(nextFilter as GenericSearchFilter | 'all')
  }

  const resetFilter = () => onFilterChange('all')

  const filteredBusinessItems = useMemo(() => {
    if (filter !== 'businesses') return []
    return searchResultsWithFilter?.pages.flatMap((page) => page as BusinessSummaryItem[]) || []
  }, [filter, searchResultsWithFilter?.pages])

  const filteredProfessionalItems = useMemo(() => {
    if (filter !== 'users') return []
    return searchResultsWithFilter?.pages.flatMap((page) => page as PublicUserData[]) || []
  }, [filter, searchResultsWithFilter?.pages])

  const visibleBusinessItems =
    filter === 'all' ? (searchResults?.businesses || []).slice(0, PREVIEW_COUNT) : filteredBusinessItems
  const visibleProfessionalItems =
    filter === 'all' ? (searchResults?.users || []).slice(0, PREVIEW_COUNT) : filteredProfessionalItems

  const shownBusinessItems = filter === 'all' ? searchResults?.businesses || [] : filteredBusinessItems
  const shownProfessionalItems = filter === 'all' ? searchResults?.users || [] : filteredProfessionalItems

  const shouldShowBusinessHistory = visibleBusinessItems.length > 0 && ['all', 'businesses'].includes(filter)
  const shouldShowProfessionalHistory = visibleProfessionalItems.length > 0 && ['all', 'users'].includes(filter)

  useEffect(() => {
    if (filter === 'all' || !inView) return
    if (!genericSearchWithFilterQuery.hasNextPage || genericSearchWithFilterQuery.isFetchingNextPage) return
    genericSearchWithFilterQuery.fetchNextPage()
  }, [filter, genericSearchWithFilterQuery, inView])

  return (
    <div className="z-10 mt-2 flex flex-col gap-4">
      <FilterPillBar
        filters={[
          { label: 'All', value: 'all' },
          { label: 'Businesses', value: 'businesses' },
          { label: 'Pros', value: 'users' },
        ]}
        activeFilter={filter}
        onFilterChange={handleFilterPillChange}
      />
      {shouldShowBusinessHistory && (
        <div className="flex flex-col gap-3">
          <SectionHeader
            title="Business"
            actionLabel={filter === 'businesses' ? 'Go back' : 'View all'}
            onViewAll={() => (filter === 'businesses' ? resetFilter() : onFilterChange('businesses'))}
            hideAction={shownBusinessItems?.length <= PREVIEW_COUNT}
          />
          <div className="flex flex-col gap-5">
            {visibleBusinessItems?.map((item) => (
              <FacelessBusinessSearchResultItem key={item.id} business={item} />
            ))}
            {filter === 'businesses' && isFetchingNextPage && (
              <>
                <FacelessBizSearchResultItemSkeleton />
                <FacelessBizSearchResultItemSkeleton />
              </>
            )}
          </div>
        </div>
      )}
      {shouldShowProfessionalHistory && (
        <div className="flex flex-col gap-3">
          <SectionHeader
            title="Professionals"
            actionLabel={filter === 'users' ? 'Go back' : 'View all'}
            onViewAll={() => (filter === 'users' ? resetFilter() : onFilterChange('users'))}
            hideAction={shownProfessionalItems?.length <= PREVIEW_COUNT}
          />
          <div className="flex flex-col gap-5">
            {visibleProfessionalItems?.map((item) => (
              <UserSearchResultItem key={item.id} user={item} />
            ))}
            {filter === 'users' && isFetchingNextPage && (
              <>
                <UserSearchResultItemSkeleton />
                <UserSearchResultItemSkeleton />
              </>
            )}
          </div>
        </div>
      )}
      {filter !== 'all' && genericSearchWithFilterQuery.hasNextPage && <div ref={loadMoreRef} className="h-2 w-full" />}
    </div>
  )
}

const GenericSearchResults = ({ searchText }: GenericSearchResultsProps) => {
  const [filter, setFilter] = useState<GenericSearchFilter | 'all'>('all')

  useEffect(() => {
    setFilter('all')
  }, [searchText])

  return (
    <Suspense fallback={<GenericSearchResultsViewSkeleton filter={filter} />}>
      <GenericSearchResultsContent searchText={searchText} filter={filter} onFilterChange={setFilter} />
    </Suspense>
  )
}

export default GenericSearchResults
